import { Measurement, MeasurementType, UnitOfMeasurement } from '@/types/helpers';
import Decimal from 'decimal.js';
import { conversionCache, getValidTypes, getValidUnits } from './unitSystems';
import { InvalidConversionError, InvalidStateError } from '../errors';
import { simpleClone } from '../utils';

export function convertMeasurement<T extends MeasurementType = 'length'>(
  measurement: Measurement<T>,
  targetUnit: UnitOfMeasurement<T>,
  outputDecimal: true,
): Decimal;
export function convertMeasurement<T extends MeasurementType = 'length'>(
  measurement: Measurement<T>,
  targetUnit: UnitOfMeasurement<T>,
  outputDecimal: false,
): Measurement<T>;
export function convertMeasurement<T extends MeasurementType = 'length'>(
  measurement: Measurement<T>,
  targetUnit: UnitOfMeasurement<T>,
): Measurement<T>;
export function convertMeasurement<T extends MeasurementType = 'length'>(
  measurement: Measurement<T>,
  targetUnit: UnitOfMeasurement<T>,
  outputDecimal: boolean = false,
): Decimal | Measurement<T> {
  if (measurement.unit === targetUnit) {
    return outputDecimal ? new Decimal(measurement.value) : simpleClone(measurement);
  }

  let targetvalue: null | Decimal = null;
  if (measurement.type === 'temperature') {
    // Handle temperatures manually for now as the data is only multiplication factors
    targetvalue = convertTemperatureValue(
      measurement.value,
      measurement.unit as UnitOfMeasurement<'temperature'>,
      targetUnit as UnitOfMeasurement<'temperature'>,
    );
  } else {
    const factors = conversionCache.get(measurement.unit);
    if (!factors) {
      throw new InvalidConversionError(`Missing conversion tables for unit ${measurement.unit}`);
    }

    if (factors[targetUnit] === undefined) {
      throw new InvalidConversionError(`Missing conversion from ${measurement.unit} to ${targetUnit}`);
    }

    targetvalue = new Decimal(measurement.value).times(factors[targetUnit]);
  }

  if (outputDecimal) {
    return targetvalue;
  }

  return {
    value: targetvalue.toDecimalPlaces(6).toNumber(),
    type: measurement.type,
    unit: targetUnit,
  };
}

const validTempUnits = ['k', 'c', 'f'];
export function convertTemperatureValue(
  value: number | string | Decimal,
  sourceUnit: UnitOfMeasurement<'temperature'>,
  targetUnit: UnitOfMeasurement<'temperature'>,
): Decimal {
  if (!validTempUnits.includes(sourceUnit) || !validTempUnits.includes(targetUnit)) {
    throw new InvalidConversionError(`Can not convert temperature from ${sourceUnit} to ${targetUnit}`);
  }

  const source = new Decimal(value);

  let cValue: null | Decimal = null;
  if (sourceUnit === 'k') {
    cValue = source.sub(273.15);
  } else if (sourceUnit === 'f') {
    cValue = source.minus(32).div(1.8);
  } else {
    cValue = source;
  }

  let tValue: null | Decimal = null;
  if (targetUnit === 'k') {
    tValue = cValue.add(273.15);
  } else if (targetUnit === 'f') {
    tValue = cValue.times(1.8).add(32);
  } else {
    tValue = cValue;
  }

  return tValue;
}

export function convertLengthValue(
  value: number,
  sourceUnit: UnitOfMeasurement<'length'>,
  targetUnit: UnitOfMeasurement<'length'>,
) {
  return convertMeasurement(
    {
      type: 'length',
      unit: sourceUnit,
      value,
    },
    targetUnit,
  ).value;
}

export function isMeasurement(obj: unknown): obj is Measurement {
  if (typeof obj !== 'object' || !obj) {
    return false;
  }
  if (!('unit' in obj) || typeof obj.unit !== 'string') {
    return false;
  }

  if (!('type' in obj) || typeof obj.type !== 'string') {
    return false;
  }

  if (!('value' in obj) || obj.value === undefined || obj.value === null || typeof obj.value !== 'number') {
    return false;
  }

  if (obj.type && !getValidTypes().has(obj.type.toLocaleLowerCase())) {
    return false;
  }

  if (obj.type !== 'other') {
    const measurementUnits = getValidUnits();
    if (measurementUnits[obj.type] && Array.isArray(measurementUnits[obj.type])) {
      if (!measurementUnits[obj.type].includes(obj.unit)) {
        return false;
      }
    }
  }

  return true;
}

export function createMeasurement<T extends MeasurementType = 'length'>(
  value: number,
  unit: UnitOfMeasurement<T>,
  type?: T,
  decimalPlaces?: number,
): Measurement<T>;
export function createMeasurement<T extends MeasurementType = 'length'>(
  value: number,
  unit: UnitOfMeasurement<T>,
  decimalPlaces?: number,
): Measurement<T>;
export function createMeasurement<T extends MeasurementType = 'length'>(
  value: number,
  unit: UnitOfMeasurement<T>,
  type?: T | number,
  decimalPlaces?: number,
): Measurement<T> {
  if (typeof type === 'number') {
    decimalPlaces = type;
    type = undefined;
  }
  type ??= 'length' as T;
  return {
    value: typeof decimalPlaces === 'number' ? new Decimal(value).toDecimalPlaces(decimalPlaces).toNumber() : value,
    unit,
    type,
  } as Measurement<T>;
}

type CalcOp = 'sub' | 'add' | 'div' | 'mul';
function baseMeasurementCalc<T extends MeasurementType>(
  a: Measurement<T>,
  b: Measurement<T>,
  targetUnit: UnitOfMeasurement<T>,
  operation: CalcOp,
  decimalPlaces = 20,
): Measurement<T> {
  if (a.type !== b.type) {
    throw new InvalidStateError(`Measurements of different types can not be calculated! [${a.type} != ${b.type}]`);
  }

  const convertedB = convertMeasurement(b, targetUnit, true);
  const convertedA = targetUnit === a.unit ? new Decimal(a.value) : convertMeasurement(a, targetUnit, true);

  return createMeasurement(
    convertedA[operation](convertedB).toDecimalPlaces(decimalPlaces).toNumber(),
    targetUnit,
    a.type,
  );
}

export const measurementCalc = {
  add: <T extends MeasurementType>(
    a: Measurement<T>,
    b: Measurement<T>,
    targetUnit: UnitOfMeasurement<T> = a.unit,
    decimalPlaces = 20,
  ) => baseMeasurementCalc(a, b, targetUnit, 'add', decimalPlaces),
  sub: <T extends MeasurementType>(
    a: Measurement<T>,
    b: Measurement<T>,
    targetUnit: UnitOfMeasurement<T> = a.unit,
    decimalPlaces = 20,
  ) => baseMeasurementCalc(a, b, targetUnit, 'sub', decimalPlaces),
  mul: <T extends MeasurementType>(
    a: Measurement<T>,
    b: Measurement<T>,
    targetUnit: UnitOfMeasurement<T> = a.unit,
    decimalPlaces = 20,
  ) => baseMeasurementCalc(a, b, targetUnit, 'mul', decimalPlaces),
  div: <T extends MeasurementType>(
    a: Measurement<T>,
    b: Measurement<T>,
    targetUnit: UnitOfMeasurement<T> = a.unit,
    decimalPlaces = 20,
  ) => baseMeasurementCalc(a, b, targetUnit, 'div', decimalPlaces),
  abs: <T extends MeasurementType>(a: Measurement<T>, decimalPlaces = 20) => {
    return {
      ...a,
      value: new Decimal(a.value).abs().toDecimalPlaces(decimalPlaces).toNumber(),
    };
  },
} as const;

export function isMeasurementEqual(a: Measurement, b: Measurement) {
  if (a.type !== b.type) return false;
  const bDecimal = convertMeasurement(b, a.unit, true).toDecimalPlaces(6);
  const aDecimal = new Decimal(a.value).toDecimalPlaces(6);

  return aDecimal.equals(bDecimal);
}
